import type React from "react";
import { getAvatarFallbackUrl, getUserAvatarUrl } from "@/utils/avatar";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";

interface UserAvatarProps {
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  className?: string;
}

/**
 * ユーザー名またはメールアドレスからイニシャルを生成
 */
function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    // 名前からイニシャルを生成（最大2文字）
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  if (email) {
    // メールアドレスの@より前の部分からイニシャルを生成
    const username = email.split("@")[0];
    return username.slice(0, 2).toUpperCase();
  }

  return "U"; // デフォルト
}

/**
 * ユーザーアバターコンポーネント
 * - ユーザー画像がある場合は表示（DiceBear形式も自動変換）
 * - ない場合はイニシャルを表示
 */
const UserAvatar: React.FC<UserAvatarProps> = ({ name, email, imageUrl, className }) => {
  const initials = getInitials(name, email);

  // DBの値（"dicebear:style:seed" または URL）を実際のURLに変換
  const avatarUrl = email ? getUserAvatarUrl(email, imageUrl) : undefined;

  return (
    <Avatar className={className}>
      {avatarUrl && (
        <AvatarImage
          src={avatarUrl}
          alt={name || email || "User"}
          onError={(e) => {
            e.currentTarget.src = getAvatarFallbackUrl();
          }}
        />
      )}
      <AvatarFallback className="bg-gray-200 text-gray-700 font-semibold text-xs">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

export default UserAvatar;
